import React, { useEffect, useState } from 'react'

const LS_KEY = 'calculationsFormulasConfigV1';

const initialConfig = {
  // Purchase Amount
  purchaseShare: true,
  purchaseDirect: true,
  purchaseOperator: "+",
  // Gross Profit B/F
  grossSaleStock: true,
  grossSaleStockOp: "+",
  grossSaleCharges: false,
  grossSaleChargesOp: "+",
  grossPurchasedStockSale: true,
  grossPurchasedStockSaleOp: "+",
  grossPurchaseIndirect: false,
  grossInterestCharges: false,
  grossInterestChargesOp: "+",
  // Indirect Expenses
  indInterestCharges: true,
  indirectChargesOp: "+",
  indPurchasedStockSale: true,
  indPurchasedStockSaleOp: "+",
  indPurchaseIndirect: true,
  // Net P&L
  netGrossProfit: true,
  netIndirectExpenses: true,
  netIndirectIncome: false, // <-- Added default for new checkbox
  netPLOperator: "-",
  // Gross Profit C/O
  gpcGrossProfit: true,
  gpcSaleAccount: true,
  gpcOperator: "-",
}

// Utility: Get data from LS or fallback
const getConfig = () => {
  try {
    const saved = window.localStorage.getItem(LS_KEY)
    if (saved) return JSON.parse(saved)
    // Save initialConfig by default on first load (if not present)
    window.localStorage.setItem(LS_KEY, JSON.stringify(initialConfig))
    return initialConfig
  } catch(e) {
    // In case of error, also try to save fallback initial value
    window.localStorage.setItem(LS_KEY, JSON.stringify(initialConfig))
    return initialConfig
  }
}

const setConfig = (cfg: any) => {
  window.localStorage.setItem(LS_KEY, JSON.stringify(cfg))
}

function useLocalConfig() {
  const [config, setCfg] = useState(getConfig());

  useEffect(() => {
    setConfig(config)
  }, [config])

  // updated logic for coupled checkboxes
  function updateConfig(key: string, value: any) {
    setCfg((cfg:any) => {
      let newCfg = { ...cfg, [key]: value };

      // Coupled checkboxes: grossInterestCharges <-> indInterestCharges
      // And coupled: grossPurchasedStockSale <-> indPurchasedStockSale
      // And coupled: grossPurchaseIndirect <-> indPurchaseIndirect
      // If one is changed, the other becomes opposite.

      if (key === "grossInterestCharges") {
        newCfg.indInterestCharges = !value;
      } else if (key === "indInterestCharges") {
        newCfg.grossInterestCharges = !value;
      }

      if (key === "grossPurchasedStockSale") {
        newCfg.indPurchasedStockSale = !value;
      } else if (key === "indPurchasedStockSale") {
        newCfg.grossPurchasedStockSale = !value;
      }

      if (key === "grossPurchaseIndirect") {
        newCfg.indPurchaseIndirect = !value;
      } else if (key === "indPurchaseIndirect") {
        newCfg.grossPurchaseIndirect = !value;
      }

      setConfig(newCfg)
      return newCfg
    })
  }

  return [config, updateConfig] as const
}

const Calculations = () => {
  const [config, setConfigVal] = useLocalConfig();

  return (
    <div className="relative bg-gradient-to-b from-[#d3c9fc] via-[#f4edff] to-[#e7e2fa] rounded-[2.5rem] shadow-[0_8px_32px_0_rgba(100,66,200,0.16)] p-12 my-12 max-w-7xl mx-auto border-2 border-[#c9bae6] ">
      <div className="absolute left-0 top-0 -translate-y-1/2 -translate-x-1/3 w-32 h-32 bg-gradient-to-tr from-[#8777ED44] to-[#e5eaff66] rounded-full blur-2xl pointer-events-none z-0"></div>
      <div className="absolute right-0 bottom-0 translate-y-1/3 translate-x-1/4 w-36 h-36 bg-gradient-to-br from-[#C4E1F7cc] to-[#eab9ff44] rounded-full blur-2xl pointer-events-none z-0"></div>
      <h2 className="relative z-10 text-4xl tracking-tight text-[#422293] font-black mb-10 flex items-center gap-3">
        <span className="bg-gradient-to-r from-[#9E77ED] to-[#6742c7] text-transparent bg-clip-text">
          <svg className="w-9 h-9 inline-block text-[#9E77ED] drop-shadow-[0_2px_4px_rgba(158,119,237,0.25)] rotate-[10deg]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M12 20c5 0 9-4 9-9 0-5-4-9-9-9S3 6 3 11c0 5 4  9 9 9Z"></path>
            <path d="M9 10h6M9 14h4"></path>
          </svg>
        </span>
        <span>Calculation Formulas</span>
      </h2>
      <div className="relative z-10 grid md:grid-cols-2 xl:grid-cols-3 gap-7">
        {/* Total Amount (not interactive) */}
        <div className="bg-white bg-opacity-85 backdrop-blur-[6px] border-2 border-[#e7e2fa] hover:scale-[1.03] transition-all duration-200 p-6 rounded-2xl shadow-xl flex flex-col items-start gap-3 group">
          <span className="flex items-center mb-2">
            <input type="checkbox" readOnly disabled className="form-checkbox accent-[#6742c7] w-6 h-6 rounded-full scale-110 shadow-inner" checked />
            <span className="ml-3 text-2xl font-semibold text-[#442293] group-hover:underline">Total Amount</span>
          </span>
          <span className="text-gray-600 text-base font-medium">Sum of all eligible calculation options</span>
        </div>

        {/* Purchase Amount */}
        <div className="bg-gradient-to-tr from-[#d7e7faee] to-[#e3d6fb88] border-2 border-[#aed8f1] hover:border-[#4f92e9] hover:scale-[1.04] transition-all duration-200 p-6 rounded-2xl shadow-lg flex flex-col gap-3 group">
          <span className="flex items-center">
            <input type="checkbox" readOnly disabled className="form-checkbox accent-[#2C72A4] w-6 h-6 rounded-full" checked />
            <span className="ml-3 text-xl font-bold text-[#2C72A4] group-hover:underline">Purchase Amount</span>
          </span>
          <div className="mt-1 space-y-1 pl-7">
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="purchase-share"
                className="accent-[#3881bb] w-4 h-4"
                checked={!!config.purchaseShare}
                onChange={e => setConfigVal('purchaseShare', e.target.checked)}
              />
              <label htmlFor="purchase-share" className="text-sm text-gray-700">Include Share Purchased</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="purchase-direct"
                className="accent-[#3881bb] w-4 h-4"
                checked={!!config.purchaseDirect}
                onChange={e => setConfigVal('purchaseDirect', e.target.checked)}
              />
              <label htmlFor="purchase-direct" className="text-sm text-gray-700">Include Direct Payment</label>
            </div>
            <div className="flex items-center gap-2 mt-2 mb-2">
              <label className="text-xs text-[#4F8CBB] font-semibold">Operation</label>
              <select
                className="text-xs font-bold px-2 py-1 rounded-xl bg-white bg-opacity-80 border border-[#bad6ea]"
                value={config.purchaseOperator}
                onChange={e => setConfigVal('purchaseOperator', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">−</option>
              </select>
              <span className="ml-1 text-[#3881bb] text-xs animate-pulse">Variables</span>
            </div>
            <div className="text-xs text-[#3881bb] bg-[#e8f3f9] rounded px-2 py-1 w-fit">
              Formula: <span className="italic">Share purchased</span>
              <span className="font-semibold mx-2">+ / -</span>
              <span className="italic">direct payment</span>
            </div>
          </div>
        </div>

        {/* Gross Profit B/F */}
        <div className="bg-gradient-to-br from-[#e7dcf7cc] to-[#f5eefc] border-2 border-[#d7c0ee] hover:border-[#8B42C7] transition-all duration-150 hover:scale-[1.035] p-6 rounded-2xl shadow-lg flex flex-col gap-3 group">
          <span className="flex items-center">
            <input type="checkbox" className="form-checkbox accent-[#8B42C7] w-6 h-6 rounded-full" checked readOnly disabled />
            <span className="ml-3 text-xl font-bold text-[#8B42C7] group-hover:text-[#6742c7]">Gross Profit B/F</span>
          </span>
          <div className="mt-1 space-y-1 pl-7">
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gross-sale-stock"
                className="accent-[#8B42C7] w-4 h-4"
                checked={!!config.grossSaleStock}
                onChange={e => setConfigVal('grossSaleStock', e.target.checked)}
              />
              <label htmlFor="gross-sale-stock" className="text-sm text-gray-700">Include Sale Stock</label>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gross-sale-charges"
                className="accent-[#8B42C7] w-4 h-4"
                checked={!!config.grossSaleCharges}
                onChange={e => setConfigVal('grossSaleCharges', e.target.checked)}
              />
              <label htmlFor="gross-sale-charges" className="text-sm text-gray-700">Include Sale Charges</label>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gross-purchased-stock-sale"
                className="accent-[#8B42C7] w-4 h-4"
                checked={!!config.grossPurchasedStockSale}
                onChange={e => setConfigVal('grossPurchasedStockSale', e.target.checked)}
              />
              <label htmlFor="gross-purchased-stock-sale" className="text-sm text-gray-700">Include Purchased Stock Sale Price</label>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gross-purchase-indirect"
                className="accent-[#8B42C7] w-4 h-4"
                checked={!!config.grossPurchaseIndirect}
                onChange={e => setConfigVal('grossPurchaseIndirect', e.target.checked)}
              />
              <label htmlFor="gross-purchase-indirect" className="text-sm text-gray-700">Include Purchase Indirect Charges</label>
            </div>
            {/* Interest Charges */}
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gross-interest-charges"
                className="accent-[#8B42C7] w-4 h-4"
                checked={!!config.grossInterestCharges}
                onChange={e => setConfigVal('grossInterestCharges', e.target.checked)}
              />
              <label htmlFor="gross-interest-charges" className="text-sm text-gray-700">Include Interest Charges</label>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="italic text-xs bg-[#f4e4ff] px-1 py-[2px] rounded">Sale stock</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#efebf9] border border-[#aa97ca]"
                value={config.grossSaleStockOp}
                onChange={e => setConfigVal('grossSaleStockOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#f4e4ff] px-1 py-[2px] rounded">Sale charges</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#efebf9] border border-[#aa97ca]"
                value={config.grossSaleChargesOp}
                onChange={e => setConfigVal('grossSaleChargesOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#f4e4ff] px-1 py-[2px] rounded">Purchased stock sale price</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#efebf9] border border-[#aa97ca]"
                value={config.grossPurchasedStockSaleOp}
                onChange={e => setConfigVal('grossPurchasedStockSaleOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#f4e4ff] px-1 py-[2px] rounded">Purchase indirect charges</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#efebf9] border border-[#aa97ca]"
                value={config.grossInterestChargesOp}
                onChange={e => setConfigVal('grossInterestChargesOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#f4e4ff] px-1 py-[2px] rounded">Interest charges</span>
            </div>
            <div className="text-xs text-[#8B42C7] bg-[#f9f4f0] rounded px-2 py-1 w-fit mt-1 border border-[#e7e2fa]">
              Formula: <span className="italic">Sale stock</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">sale charges</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">purchased stock sale price</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">purchase indirect charges</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">interest charges</span>
            </div>
          </div>
        </div>

        {/* Indirect Expenses */}
        <div className="bg-gradient-to-br from-[#b7f7df77] to-[#e6f8f0] border-2 border-[#bbf5e2] hover:border-[#1CAC70] transition-all duration-150 hover:scale-[1.035] p-6 rounded-2xl shadow-lg flex flex-col gap-3 group">
          <span className="flex items-center">
            <input type="checkbox" className="form-checkbox accent-[#1CAC70] w-6 h-6 rounded-full" checked readOnly disabled />
            <span className="ml-3 text-xl font-bold text-[#1CAC70] group-hover:underline">Indirect Expenses</span>
          </span>
          <div className="mt-1 space-y-1 pl-7">
            {/* REWRITE: "Include Indirect Charges" → "Include Interest Charges" */}
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="ind-interest-charges"
                className="accent-[#1CAC70] w-4 h-4"
                checked={!!config.indInterestCharges}
                onChange={e => setConfigVal('indInterestCharges', e.target.checked)}
              />
              <label htmlFor="ind-interest-charges" className="text-sm text-gray-700">Include Interest Charges</label>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="ind-purchased-stock-sale"
                className="accent-[#1CAC70] w-4 h-4"
                checked={!!config.indPurchasedStockSale}
                onChange={e => setConfigVal('indPurchasedStockSale', e.target.checked)}
              />
              <label htmlFor="ind-purchased-stock-sale" className="text-sm text-gray-700">Include Purchased Stock Sale Price</label>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="ind-purchase-indirect"
                className="accent-[#1CAC70] w-4 h-4"
                checked={!!config.indPurchaseIndirect}
                onChange={e => setConfigVal('indPurchaseIndirect', e.target.checked)}
              />
              <label htmlFor="ind-purchase-indirect" className="text-sm text-gray-700">Include Purchase Indirect Charges</label>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="italic text-xs bg-[#e9fff7] px-1 py-[2px] rounded">Interest charges</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#e3fef1] border border-[#90edc0]"
                value={config.indirectChargesOp}
                onChange={e => setConfigVal('indirectChargesOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#e9fff7] px-1 py-[2px] rounded">Purchased stock sale price</span>
              <select
                className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-[#e3fef1] border border-[#90edc0]"
                value={config.indPurchasedStockSaleOp}
                onChange={e => setConfigVal('indPurchasedStockSaleOp', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">-</option>
              </select>
              <span className="italic text-xs bg-[#e9fff7] px-1 py-[2px] rounded">Purchase indirect charges</span>
            </div>
            <div className="text-xs text-[#1CAC70] bg-[#eafdff] rounded px-2 py-1 w-fit mt-1 border border-[#d7c0ee]">
              Formula: <span className="italic">Interest charges</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">purchased stock sale price</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">purchase indirect charges</span>
            </div>
          </div>
        </div>

        {/* Net P&L */}
        <div className="bg-gradient-to-tr from-[#faecd6dd] to-[#fdf5ec] border-2 border-[#FFD6A3] hover:border-[#E89237] transition-all duration-150 hover:scale-[1.035] p-6 rounded-2xl shadow-lg flex flex-col gap-3 group">
          <span className="flex items-center">
            <input type="checkbox" className="form-checkbox accent-[#E89237] w-6 h-6 rounded-full" checked readOnly disabled />
            <span className="ml-3 text-xl font-bold text-[#D97706] group-hover:text-[#E89237] group-hover:underline">Net P&amp;L</span>
          </span>
          <div className="mt-1 space-y-1 pl-7">
            {/* Add indirect income checkbox here */}
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="net-gross-profit"
                className="accent-[#E89237] w-4 h-4"
                checked={!!config.netGrossProfit}
                onChange={e => setConfigVal('netGrossProfit', e.target.checked)}
              />
              <label htmlFor="net-gross-profit" className="text-sm text-gray-700">Include Gross Profit B/F</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="net-indirect-expenses"
                className="accent-[#E89237] w-4 h-4"
                checked={!!config.netIndirectExpenses}
                onChange={e => setConfigVal('netIndirectExpenses', e.target.checked)}
              />
              <label htmlFor="net-indirect-expenses" className="text-sm text-gray-700">Include Indirect Expenses</label>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="checkbox"
                id="net-indirect-income"
                className="accent-[#E89237] w-4 h-4"
                checked={!!config.netIndirectIncome}
                onChange={e => setConfigVal('netIndirectIncome', e.target.checked)}
              />
              <label htmlFor="net-indirect-income" className="text-sm text-gray-700">Include Indirect Income</label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <label className="text-xs text-[#E89237] font-semibold">Operator:</label>
              <select
                className="text-xs font-bold px-2 py-1 rounded-xl bg-white bg-opacity-75 border border-[#F5D0B0]"
                value={config.netPLOperator}
                onChange={e => setConfigVal('netPLOperator', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">−</option>
              </select>
              <span className="italic text-xs ml-2 text-[#b48b54]">
                Gross profit b/f <span className="font-semibold mx-1">+ / -</span> Indirect Expenses
                {config.netIndirectIncome ? (
                  <><span className="font-semibold mx-1">+ / -</span> Indirect Income</>
                ) : null}
              </span>
            </div>
            <div className="text-xs text-[#E89237] bg-[#f9f4f0] rounded px-2 py-1 w-fit mt-1 border border-[#FFD6A3]">
              Formula: <span className="italic">Gross profit b/f</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">Indirect Expenses</span>
              {config.netIndirectIncome ? (
                <>
                  <span className="font-semibold mx-1">+ / -</span>
                  <span className="italic">Indirect Income</span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Gross Profit C/O */}
        <div className="bg-gradient-to-br from-[#fff6deDD] to-[#fff7eb] border-2 border-[#f4e6cb] hover:border-[#D97706] transition-all duration-150 hover:scale-[1.03] p-6 rounded-2xl shadow-lg flex flex-col gap-3 group">
          <span className="flex items-center">
            <input type="checkbox" className="form-checkbox accent-[#D97706] w-6 h-6 rounded-full" checked readOnly disabled />
            <span className="ml-3 text-xl font-bold text-[#D97706] group-hover:underline">Gross Profit C/O</span>
          </span>
          <div className="mt-1 space-y-1 pl-7">
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="gpc-gross-profit"
                className="accent-[#D97706] w-4 h-4"
                checked={!!config.gpcGrossProfit}
                onChange={e => setConfigVal('gpcGrossProfit', e.target.checked)}
              />
              <label htmlFor="gpc-gross-profit" className="text-sm text-gray-700">Include Gross Profit B/F</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="gpc-sale-account"
                className="accent-[#D97706] w-4 h-4"
                checked={!!config.gpcSaleAccount}
                onChange={e => setConfigVal('gpcSaleAccount', e.target.checked)}
              />
              <label htmlFor="gpc-sale-account" className="text-sm text-gray-700">Include Sale Account</label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <label className="text-xs text-[#D97706] font-semibold">Operator:</label>
              <select
                className="text-xs font-bold px-2 py-1 rounded-xl bg-white bg-opacity-65 border border-[#efdfc8]"
                value={config.gpcOperator}
                onChange={e => setConfigVal('gpcOperator', e.target.value)}
              >
                <option value="+">+</option>
                <option value="-">−</option>
              </select>
              <span className="italic text-xs ml-2 text-[#b48b54]">Gross profit b/f <span className="font-semibold mx-1">+ / -</span> Sale account</span>
            </div>
            <div className="text-xs text-[#D97706] bg-[#f9f4f0] rounded px-2 py-1 w-fit mt-1 border border-[#FFD6A3]">
              Formula: <span className="italic">Gross profit b/f</span> <span className="font-semibold mx-1">+ / -</span>
              <span className="italic">Sale account</span>
            </div>
          </div>
        </div>
      </div>
      <div className="relative z-10 text-right pt-6 mt-3">
        <span className="text-md text-[#9E77ED] bg-white bg-opacity-70 rounded-xl py-2 px-5 shadow border border-[#e4dbf8] inline-flex items-center gap-2">
          <span className="font-bold">*</span>
          <span>
            For each formula, <span className="font-semibold text-[#6742c7]">select which items</span> to include and choose <span className="font-semibold">+ / -</span> between them.<br />
            <span className="text-xs text-[#6742c7] font-medium">(Your choices are automatically saved in your browser)</span>
          </span>
        </span>
      </div>
    </div>
  )
}

export default Calculations